import React from "react";
import { Button, PaginationSimple } from "@/components/ui";
import { Media, PostDetails, PostSocialNetworkDetails } from "@/services/bedrock";
import { MediaPlayer } from "@/components";
import { Download, Loader2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

interface MediaPanelProps {
  post: PostDetails;
  socialPost?: PostSocialNetworkDetails;
}

export function MediaPanel({ post, socialPost }: MediaPanelProps) {
  const [currentMediaPage, setCurrentMediaPage] = React.useState(1);
  const [downloadingId, setDownloadingId] = React.useState<number | null>(null);

  const mediaItems = React.useMemo(() => {
    return (post?.media || []).sort((m1: Media, m2: Media) => m1.sortOrder - m2.sortOrder);
  }, [post?.media]);

  const currentMedia = mediaItems[currentMediaPage - 1];
  const totalMedia = mediaItems.length;

  const handleDownload = React.useCallback(
    async (media: Media) => {
      if (!media.uri) return;
      setDownloadingId(media.id);
      try {
        const response = await fetch(media.uri);
        const blob = await response.blob();
        const urlObject = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlObject;

        const ext = media.extension || (media.category === "IMAGE" ? "png" : "mp4");
        const filename = `${uuidv4()}.${ext}`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlObject);
      } catch (error) {
        console.error("Failed to download media:", error);
        window.open(media.uri, "_blank", "noopener,noreferrer");
      } finally {
        setDownloadingId(null);
      }
    },
    [post?.id]
  );

  return (
    <div className="flex flex-col bg-slate-50 border-r border-slate-100 h-[400px]">
      <div className="flex-1 flex items-center justify-center overflow-hidden m-3 rounded-xl bg-white ring-1 ring-slate-200 shadow-inner relative">
        {currentMedia ? (
          <div className="reviewMediaSection w-full h-full flex items-center justify-center">
            <div className="postMediaContainer w-full">
              <MediaPlayer media={currentMedia} previewOnly={true} />
            </div>
          </div>
        ) : (
          <div className="py-20 flex flex-col items-center gap-2 text-muted-foreground">
            <span className="text-sm italic">No media attached</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 p-3 pt-0">
        {currentMedia && (
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs"
            disabled={downloadingId === currentMedia.id}
            onClick={() => handleDownload(currentMedia)}
          >
            {downloadingId === currentMedia.id ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <Download className="mr-1.5 size-3.5" />
            )}
            Download Media
          </Button>
        )}

        {totalMedia > 1 && (
          <PaginationSimple
            page={currentMediaPage}
            totalPages={totalMedia}
            onPageChange={setCurrentMediaPage}
            hideLabel={true}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
}
